/**
 * @file 働く姿の節 — 資格の試験の先にある働き方を、折りたたんで出す。
 *
 * @what: 資格のカリキュラムが持つ働き方を、活かし方・試験の先に必要なもの・年収の幅と出どころ・
 *   雇用の形と需要・肉体と精神の負荷・前の働き方からの移り方の表で出す。既定は閉じておく。
 * @why: 資格の平均年収だけでは、その資格でどう働けるかが見えない。「資格を持っている」と「いまどう
 *   活かしているか」の組で見ると登り方が分かる (代表の要望、2026-09-11)。おとなの学ぶ人のときだけ
 *   出し、子ども向けの見せ方には置かない。年齢では切らない (設計 §4.2)。
 * @failure: 年収を出せない働き方があるので、幅が空でも節が成立する。空欄は「—」にする。
 */

import { useState } from "react";
import type { StudyTextbookRouteCareer } from "../api/textbookRoutes";
import {
  CAREER_USE_LABELS,
  careerLoadLabel,
  incomeRangeLabel,
  requirementCareerCount,
} from "../model/careers";
import "./study.css";

export function CareersPanel({ careers }: { careers: readonly StudyTextbookRouteCareer[] }) {
  const [open, setOpen] = useState(false);
  if (careers.length === 0) return null;
  // 法律上の要件になる働き方の数は、この節でいちばん伝えたい事実である。
  const required = requirementCareerCount(careers);
  return (
    <section className="study-careers">
      <button
        type="button"
        className="study-ghost-btn"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "働く姿を閉じる" : `働く姿を見る（${careers.length}通り）`}
      </button>
      {open ? (
        <>
          <p className="study-section-help">
            試験の先にある働き方です。資格が法律上の要件になるのは{required}通りで、残りは知識か
            肩書として使います。年収は幅で持ち、出どころを併記しています。負荷は求人と体験談から
            置いた目安です。
          </p>
          <div className="study-careers-scroll">
            <table className="study-careers-table">
              <thead>
                <tr>
                  <th scope="col">働き方</th>
                  <th scope="col">活かし方</th>
                  <th scope="col">年収の目安</th>
                  <th scope="col">肉体</th>
                  <th scope="col">精神</th>
                </tr>
              </thead>
              <tbody>
                {careers.map((c) => {
                  const income = incomeRangeLabel(c);
                  return (
                    <tr key={c.name}>
                      <th scope="row">
                        <span className="study-careers-name">
                          {c.source_url ? (
                            <a href={c.source_url} target="_blank" rel="noreferrer">
                              {c.name}
                            </a>
                          ) : (
                            c.name
                          )}
                        </span>
                        {c.prerequisite ? (
                          <span className="study-careers-note">先に必要: {c.prerequisite}</span>
                        ) : null}
                        {c.stability ? (
                          <span className="study-careers-note">{c.stability}</span>
                        ) : null}
                        {c.path ? <span className="study-careers-note">{c.path}</span> : null}
                      </th>
                      <td>{CAREER_USE_LABELS[c.use]}</td>
                      <td>
                        {income ?? "—"}
                        {c.income_source ? (
                          <span className="study-careers-note">{c.income_source}</span>
                        ) : null}
                      </td>
                      <td>{careerLoadLabel(c.load_physical) ?? "—"}</td>
                      <td>{careerLoadLabel(c.load_mental) ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
